import { ArrowUpRight, CaretDown } from "@phosphor-icons/react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { cn } from "../lib/cn";
import { CARD_BODY, CARD_EYEBROW, CARD_ROW_TITLE } from "../primitives/type";
import type { QuietCardHeartbeat, QuietCardWatchRow } from "./quiet-card";

/** What Heartbeat is sensing, as its own card under the one saying nothing needs doing. */

/** Whether the sense card will draw anything. */
export function hasHeartbeatSense(
  watching: ReadonlyArray<QuietCardWatchRow>,
  heartbeat: QuietCardHeartbeat | null,
): boolean {
  return watching.length > 0 || heartbeat !== null;
}

/** Always the treatment of the card above, so the pair reads as one object. */
export type HeartbeatSenseTone = "dark" | "light";

export interface HeartbeatSenseCardProps {
  watching: ReadonlyArray<QuietCardWatchRow>;
  tone: HeartbeatSenseTone;
  heartbeat: QuietCardHeartbeat | null;
  /** Header right slot: the soonest timing, or when quiet hours lift. */
  timingLabel?: string | null;
  /** Header label, which changes while quiet hours are on. */
  title?: string;
  className?: string;
  /** The reasoning history, revealed on expand. */
  timeline?: ReactNode;
  /** Told when the history opens, so the caller can fetch it only then. */
  onToggleHistory?: (open: boolean) => void;
}

export function HeartbeatSenseCard({
  watching,
  tone,
  heartbeat,
  timingLabel = null,
  title = "Keeping an eye on",
  className,
  timeline,
  onToggleHistory,
}: HeartbeatSenseCardProps) {
  const [open, setOpen] = useState(false);
  const head = useRef<HTMLDivElement>(null);
  // After commit, not inside the click: a rAF from the handler still measures
  // the collapsed layout, so the page moved by the height of a closed card.
  useEffect(() => {
    if (!open) return;
    head.current?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "start",
    });
  }, [open]);
  if (!hasHeartbeatSense(watching, heartbeat)) return null;
  const dark = tone === "dark";
  const toggle = () => {
    const next = !open;
    setOpen(next);
    onToggleHistory?.(next);
  };
  return (
    <section
      className={cn(
        // Flat where it meets the card above: the seam is what makes the two read
        // as one object rather than two tiles that happen to be stacked.
        // Positioned and tucked up a pixel so it paints over the card above's
        // shadow and the anti-aliased row a fractional height leaves.
        "relative -mt-px flex w-full min-w-0 flex-col gap-3 rounded-2xl rounded-t-none p-5",
        // Same geometry and lift either way; only the surface changes, so the
        // pair reads as one object whichever state the card above is in.
        // The light surface is the quiet card's own, with no top edge, so the
        // join shows no line and no rounded notch.
        dark
          ? "border border-ink-on-media/12 bg-scrim shadow-card"
          : "border border-t-0 border-border-card bg-surface-ambient shadow-card",
        className,
      )}
      aria-label="What Heartbeat is sensing"
    >
      <div className="flex items-center justify-between gap-3">
        <span className={cn(CARD_EYEBROW, dark ? "text-ink-on-media/60" : "text-ink-eyebrow")}>
          {title}
        </span>
        <span className="flex min-w-0 shrink-0 items-center gap-3">
          {timingLabel ? (
            <span className={cn(CARD_EYEBROW, dark ? "text-ink-on-media/45" : "text-ink-tertiary")}>
              {timingLabel}
            </span>
          ) : null}
          {heartbeat ? (
            <button
              type="button"
              onClick={heartbeat.onOpenMap}
              className={cn(
                CARD_EYEBROW,
                "group inline-flex shrink-0 items-center gap-1.5 transition-colors duration-200",
                dark
                  ? "text-ink-on-media/55 hover:text-ink-on-media"
                  : "text-ink-tertiary hover:text-ink-brand",
              )}
            >
              See the map
              <ArrowUpRight
                size={12}
                weight="bold"
                aria-hidden="true"
                className="transition-transform duration-200 group-hover:translate-x-px group-hover:-translate-y-px"
              />
            </button>
          ) : null}
        </span>
      </div>

      {watching.length > 0 ? (
        <ul className="m-0 grid list-none gap-3 p-0">
          {watching.map((row) => (
            <li key={row.id} className="grid gap-1">
              <span className="flex items-baseline gap-2">
                <b
                  className={cn(
                    CARD_EYEBROW,
                    "shrink-0",
                    dark ? "text-ink-on-media/55" : "text-ink-tertiary",
                  )}
                >
                  {row.label}
                </b>
                <span
                  className={cn(
                    CARD_BODY,
                    "min-w-0 flex-1",
                    dark ? "text-ink-on-media/80" : "text-ink-body",
                  )}
                >
                  {row.reading}
                </span>
                {row.timing ? (
                  <span
                    className={cn(
                      CARD_EYEBROW,
                      "shrink-0",
                      dark ? "text-ink-on-media/45" : "text-ink-tertiary",
                    )}
                  >
                    {row.timing}
                  </span>
                ) : null}
              </span>
              <span className={cn(CARD_ROW_TITLE, dark ? "text-ink-on-media" : "text-ink-primary")}>
                {row.move}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {timeline || onToggleHistory ? (
        <div
          ref={head}
          className={cn(
            "mt-auto scroll-mt-4 border-t pt-3",
            dark ? "border-ink-on-media/12" : "border-border-default",
          )}
        >
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            className={cn(
              CARD_EYEBROW,
              "inline-flex items-center gap-1.5 transition-colors duration-200",
              dark
                ? "text-ink-on-media/75 hover:text-ink-on-media"
                : "text-ink-brand hover:text-teal-800",
            )}
          >
            {open ? "Hide what it decided" : "What it decided"}
            <CaretDown
              size={12}
              weight="bold"
              aria-hidden="true"
              className={cn("transition-transform duration-200", open && "rotate-180")}
            />
          </button>
          {open && timeline ? (
            <div className="pt-3 motion-safe:animate-fade-in-up">{timeline}</div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
